import {
  camelCase,
  extend,
  find,
  isFunction,
  isPlainObject,
  kebabCase,
  last,
  reduce,
  snakeCase,
  split,
  toInteger
} from 'lodash';
import type { Ancestry, CaseFormat, EntityData, JsonApi, Options, Relationship } from './types';

/**
 * Apply a given casing to a `string`/`attributeKey`
 *
 * @param key an attribute key
 * @param format the format to apply, either by name or function
 * @returns cased string
 */
export const formatCasing = (key: string, format?: CaseFormat): string => {
  if (isFunction(format)) return format(key);

  switch (format) {
    case 'dash-case':
    case 'lisp-case':
    case 'spinal-case':
    case 'kebab-case':
      return kebabCase(key);
    case 'underscore_case':
    case 'snake_case':
      return snakeCase(key);
    case 'camelCase':
      return camelCase(key);
    default:
      return key;
  }
};

/**
 * Check if parameter is an array or object,
 * these often need to be handled in a different way
 */
export const isComplexType = (obj: unknown): boolean => Array.isArray(obj) || isPlainObject(obj);

class DeserializerProcess {
  constructor(
    private readonly jsonapi: JsonApi,
    private readonly data: EntityData,
    private readonly options: Options
  ) {}

  /**
   * Given a relationship and its included data: return the data object of the relation ship.
   *
   * If included data is not provided, fallback data is an `id` generated from the `relationship` / `entity_id`
   */
  public getValueForRelationship = (
    relationshipData: Relationship,
    included?: Record<string, unknown>
  ): Record<string, unknown> =>
    included || {
      [formatCasing('_id', this.options.keyFormat)]: toInteger(
        last(split(relationshipData.id, '/'))
      ),
      ...this.extractAttributes(relationshipData)
    };

  public processAttribute = <Attribute extends Record<string, unknown>[] | Record<string, unknown>>(
    attribute: Attribute
  ): Attribute => {
    if (Array.isArray(attribute)) {
      return attribute.map((a) => (isComplexType(a) ? this.processAttribute(a) : a)) as Attribute;
    }

    return reduce(
      attribute as Record<string, Attribute>,
      (result, value, key) => ({
        ...result,
        [formatCasing(key, this.options.keyFormat)]: isComplexType(value)
          ? this.processAttribute(value)
          : value
      }),
      {}
    ) as Attribute;
  };

  /**
   * Takes an entity and processes/flattens it.
   * - renames attributes
   * - renames id & type
   * @param entity JSON:API entity to process
   * @returns processed object
   */
  public extractAttributes = (entity: EntityData): Record<string, unknown> => ({
    ...this.processAttribute(entity.attributes || {}),
    ...this.extractEntityInfo(entity)
  });

  /**
   * Takes an entity and processes/flattens it.
   *  - only extracts entity info e.g. id, type & meta
   * @param entity JSON:API entity to process
   * @returns
   */
  public extractEntityInfo = (entity: EntityData): Record<string, unknown> => ({
    ...(!!entity.attributes?._id && {
      [formatCasing('_id', this.options.keyFormat)]: entity.attributes?._id
    }),
    ...(!!this.options.id && !!entity.id && { [this.options.id]: entity.id }),
    ...(!!this.options.type && !!entity.type && { [this.options.type]: entity.type }),
    ...(!!entity.meta && { meta: entity.meta })
  });

  /**
   * Takes a relationship and extracts just the entity info
   * - does not include attributes or other relationships
   * - only id & type
   * @param entity JSON:API entity to process
   * @returns
   */
  public extractRelationshipEntityInfo = (
    entity: EntityData
  ): Promise<Record<string, unknown>> | undefined => {
    if (!entity.relationships) {
      return undefined;
    }
    const output = {};
    return Promise.all(
      Object.entries(entity.relationships).map(([key, relationship]) => {
        if (relationship.data === null) {
          output[formatCasing(key, this.options.keyFormat)] = null;
          return Promise.resolve();
        }
        if (Array.isArray(relationship.data)) {
          return Promise.all(
            relationship.data.map((relationshipData) =>
              this.extractIncludesOnlyEntityInfo(relationshipData)
            )
          ).then((includes) => {
            if (includes) {
              output[formatCasing(key, this.options.keyFormat)] = includes;
            }
          });
        }
        return Promise.resolve(this.extractIncludesOnlyEntityInfo(relationship.data)).then(
          (includes) => {
            if (includes) {
              output[formatCasing(key, this.options.keyFormat)] = includes;
            }
          }
        );
      })
    ).then(() => output);
  };

  /**
   * Takes a relationship and extracts all  entity info, attributes, & relationships
   * @param entity JSON:API entity to process
   * @param ancestry History object - helps prevent infinite recursion
   * @returns
   */
  public extractRelationships = (
    entity: EntityData,
    ancestry: Ancestry
  ): Promise<Record<string, unknown>> | undefined => {
    if (!entity.relationships) {
      return undefined;
    }
    const output = {};
    return Promise.all(
      Object.entries(entity.relationships).map(([key, relationship]) => {
        if (relationship.data === null) {
          output[formatCasing(key, this.options.keyFormat)] = null;
          return Promise.resolve();
        }

        if (Array.isArray(relationship.data)) {
          return Promise.all(
            relationship.data.map((relationshipData) =>
              this.extractIncludes(relationshipData, ancestry)
            )
          ).then((includes) => {
            if (includes) {
              output[formatCasing(key, this.options.keyFormat)] = includes;
            }
          });
        }

        return this.extractIncludes(relationship.data, ancestry).then((includes) => {
          if (includes) {
            output[formatCasing(key, this.options.keyFormat)] = includes;
          }
        });
      })
    ).then(() => output);
  };

  /**
   * Given a relationship include only the entity info
   * @param relationship
   * @returns
   */
  public extractIncludesOnlyEntityInfo = (
    relationship: Relationship
  ): Promise<Record<string, unknown> | undefined> =>
    new Promise<Record<string, unknown> | undefined>((resolve) => {
      const included = find(this.jsonapi.included, {
        id: relationship?.id,
        type: relationship?.type
      }) as EntityData | undefined;

      if (!this.jsonapi.included || !included) {
        resolve(undefined);
      } else {
        Promise.resolve(this.extractEntityInfo(included)).then((data) => {
          resolve(data);
        });
      }
    }).then((included) =>
      relationship || included ? this.getValueForRelationship(relationship, included) : undefined
    );

  /**
   * Given a relationship find the included attributes & sub-relationships
   * @param relationship
   * @param ancestry History object - helps prevent infinite recursion
   * @returns
   */
  public extractIncludes = (
    relationship: Relationship,
    ancestry: Ancestry
  ): Promise<Record<string, unknown> | undefined> =>
    new Promise<Record<string, unknown> | undefined>((resolve) => {
      const included = find(this.jsonapi.included, {
        id: relationship?.id,
        type: relationship?.type
      }) as EntityData | undefined;

      if (!this.jsonapi.included || !included) {
        resolve(undefined);
        return;
      }

      /**
       * To prevent circular references, check if the record
       * has already been processed in this thread
       *
       * If so extract the attributes and only relationship entity info
       * this results in a little duplication of data for accessing convenience
       */
      if (ancestry.has(`${included?.type}-${included?.id}`)) {
        Promise.all([
          this.extractAttributes(included),
          this.extractRelationshipEntityInfo(included)
        ]).then(([attributes, relationships]) => {
          resolve(extend(attributes, relationships));
        });
        return;
      }

      Promise.all([
        this.extractAttributes(included),
        this.extractRelationships(included, ancestry.add(`${included.type}-${included.id}`))
      ]).then(([attributes, relationships]) => {
        resolve(extend(attributes, relationships));
      });
    }).then((included) =>
      relationship || included ? this.getValueForRelationship(relationship, included) : undefined
    );

  /**
   * Process this instances data object
   * @returns deserialized object
   */
  public process = (): Promise<Record<string, unknown>> =>
    Promise.all([
      this.extractAttributes(this.data),
      this.extractRelationships(
        this.data,
        new Set<string>().add(`${this.data.type}-${this.data.id}`)
      )
    ]).then(([attributes, relationships]) => {
      const record = {
        ...attributes,
        ...relationships,
        // Links - only add field if there are links
        ...(this.jsonapi.links ? { links: this.jsonapi.links } : {})
      };

      return record;
    });
}

export default DeserializerProcess;
